import { useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { CheckCircle, FileText, Star, Award, Upload } from 'lucide-react';
import { getAllDrivers, getPendingDrivers, approveDriver, getDriverDocuments, reviewDocument } from '../api/drivers';
import { getDocumentTypes } from '../api/documentTypes';
import { useToast } from '../context/ToastContext';
import { Table } from '../components/ui/Table';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Drawer from '../components/ui/Drawer';
import Pagination from '../components/ui/Pagination';
import UploadDocumentsModal from '../components/drivers/UploadDocumentsModal';
import SearchInput from '../components/ui/SearchInput';
import useDebouncedValue from '../hooks/useDebouncedValue';
import { formatDate, getStatusColor } from '../lib/utils';
import clsx from 'clsx';

function DocumentReview({ driverId, onClose }) {
  const { addToast } = useToast();
  const qc = useQueryClient();
  const [rejectionReason, setRejectionReason] = useState('');
  const [reviewingDocId, setReviewingDocId] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['driver-documents', driverId],
    queryFn: () => getDriverDocuments(driverId).then((r) => r.data),
    enabled: !!driverId,
  });

  // The API only returns doc_type_id (an FK) on each uploaded document, not
  // its name — look the name up client-side from the same document-types
  // list the Document Types page manages (shares its query cache).
  const { data: docTypesData } = useQuery({
    queryKey: ['document-types'],
    queryFn: () => getDocumentTypes({ include_inactive: true }).then((r) => r.data),
  });
  const docTypes = Array.isArray(docTypesData) ? docTypesData : (docTypesData?.items ?? docTypesData?.document_types ?? []);
  const docTypeTitleById = Object.fromEntries(docTypes.map((dt) => [dt.id, dt.title]));

  const docs = Array.isArray(data) ? data : (data?.documents ?? data?.items ?? []);

  const review = useMutation({
    mutationFn: ({ docId, status, reason }) =>
      reviewDocument(docId, { status, rejection_reason: reason || undefined }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['driver-documents', driverId] });
      addToast(`Document ${vars.status}`, vars.status === 'approved' ? 'success' : 'warning');
      setReviewingDocId(null);
    },
    onError: () => addToast('Review failed', 'error'),
  });

  return (
    <div className="px-6 pt-4 pb-8 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {isLoading ? 'Loading documents…' : `${docs.length} uploaded`}
        </p>
        <Button size="sm" icon={Upload} disabled={!driverId} onClick={() => setUploadOpen(true)}>
          Upload Docs
        </Button>
      </div>

      {!isLoading && docs.length === 0 && (
        <div className="py-8 text-center text-sm text-gray-400">No documents uploaded</div>
      )}

      <UploadDocumentsModal
        isOpen={uploadOpen}
        onClose={() => setUploadOpen(false)}
        driverId={driverId}
        docs={docs}
      />

      {docs.map((doc) => (
        <div key={doc.id} className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <FileText size={16} className="text-gray-400" />
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{docTypeTitleById[doc.doc_type_id] ?? doc.document_type ?? doc.title ?? `Document #${doc.id}`}</span>
            </div>
            <Badge variant={getStatusColor(doc.status)}>{doc.status ?? 'pending'}</Badge>
          </div>
          {doc.file_url && (
            <a
              href={doc.file_url}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-xs text-amber-600 dark:text-amber-400 hover:underline"
            >
              View file ↗
            </a>
          )}
          {doc.status === 'pending' && (
            <div className="flex flex-col gap-2">
              <textarea
                placeholder="Rejection reason (optional)"
                className="w-full text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
                rows={2}
                value={reviewingDocId === doc.id ? rejectionReason : ''}
                onChange={(e) => { setReviewingDocId(doc.id); setRejectionReason(e.target.value); }}
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="success"
                  loading={review.isPending && reviewingDocId === doc.id}
                  onClick={() => review.mutate({ docId: doc.id, status: 'approved' })}
                >
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  loading={review.isPending && reviewingDocId === doc.id}
                  onClick={() => review.mutate({ docId: doc.id, status: 'rejected', reason: rejectionReason })}
                >
                  Reject
                </Button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function DriverTable({ data, loading, onApprove, onDocs, approvingId, emptyMessage = 'No drivers found' }) {
  const columns = [
    { key: 'id', label: 'ID', render: (v) => <span className="font-mono text-xs text-gray-500">#{v}</span> },
    {
      key: 'full_name', label: 'Name',
      render: (v, row) => <span className="font-medium text-gray-900 dark:text-gray-100">{v ?? row.name ?? row.user?.full_name ?? '—'}</span>,
    },
    { key: 'phone', label: 'Phone', render: (v, row) => v ?? row.user?.phone ?? '—' },
    {
      key: 'is_approved', label: 'Approved',
      render: (v) => <Badge variant={v ? 'success' : 'warning'}>{v ? 'Approved' : 'Pending'}</Badge>,
    },
    {
      key: 'rating', label: 'Rating',
      render: (v) => v ? (
        <span className="flex items-center gap-1 text-sm">
          <Star size={13} className="text-yellow-400 fill-yellow-400" />
          {Number(v).toFixed(1)}
        </span>
      ) : '—',
    },
    {
      key: 'is_gold', label: 'Gold',
      render: (v) => v ? <Award size={16} className="text-yellow-500" /> : <span className="text-gray-300">—</span>,
    },
    { key: 'created_at', label: 'Joined', render: (v) => <span className="text-xs text-gray-500 whitespace-nowrap">{formatDate(v)}</span> },
    {
      key: 'actions', label: 'Actions',
      render: (_, row) => (
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          {!row.is_approved && (
            <Button
              size="xs"
              variant="success"
              icon={CheckCircle}
              loading={approvingId === row.id}
              onClick={() => onApprove(row.id)}
            >
              Approve
            </Button>
          )}
          <Button size="xs" variant="secondary" icon={FileText} onClick={() => onDocs(row.id)}>
            Docs
          </Button>
        </div>
      ),
    },
  ];

  return <Table columns={columns} data={data} loading={loading} emptyMessage={emptyMessage} />;
}

const PAGE_SIZE = 20;

export default function Drivers() {
  const { addToast } = useToast();
  const qc = useQueryClient();
  const [tab, setTab] = useState('all');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [docsDriverId, setDocsDriverId] = useState(null);
  const [approvingId, setApprovingId] = useState(null);

  const debouncedSearch = useDebouncedValue(search.trim());
  const searchParam = debouncedSearch ? { search: debouncedSearch } : {};

  const { data: allData, isLoading: allLoading } = useQuery({
    queryKey: ['drivers', page, debouncedSearch],
    queryFn: () => getAllDrivers({ page, limit: PAGE_SIZE, ...searchParam }).then((r) => r.data),
    placeholderData: keepPreviousData,
  });

  const { data: pendingData, isLoading: pendingLoading } = useQuery({
    queryKey: ['drivers', 'pending', debouncedSearch],
    queryFn: () => getPendingDrivers(searchParam).then((r) => r.data),
    enabled: tab === 'pending',
    placeholderData: keepPreviousData,
  });

  function handleSearchChange(value) {
    setSearch(value);
    setPage(1);
  }

  const approve = useMutation({
    mutationFn: (id) => approveDriver(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['drivers'] });
      addToast('Driver approved!', 'success');
      setApprovingId(null);
    },
    onError: () => { addToast('Approval failed', 'error'); setApprovingId(null); },
  });

  function handleApprove(id) {
    setApprovingId(id);
    approve.mutate(id);
  }

  const allDrivers = Array.isArray(allData) ? allData : (allData?.items ?? allData?.drivers ?? []);
  const pendingDrivers = Array.isArray(pendingData) ? pendingData : (pendingData?.items ?? pendingData?.drivers ?? []);
  const allTotal = Array.isArray(allData) ? allDrivers.length : (allData?.total ?? allDrivers.length);

  const emptyMessage = debouncedSearch ? `No drivers match “${debouncedSearch}”` : 'No drivers found';

  const tabs = [
    { key: 'all', label: 'All Drivers', count: allTotal },
    { key: 'pending', label: 'Pending Approval', count: pendingDrivers.length },
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Drivers</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">Manage driver accounts and approvals</p>
        </div>
        <SearchInput
          placeholder="Search by name, phone, email or ID…"
          value={search}
          onChange={handleSearchChange}
        />
      </div>

      <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-xl w-fit">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={clsx(
              'px-4 py-1.5 rounded-lg text-sm font-medium transition-colors',
              tab === t.key
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            )}
          >
            {t.label}
            {t.count > 0 && (
              <span className="ml-1.5 text-xs bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 rounded-full px-1.5 py-0.5">
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === 'all' ? (
        <>
          <DriverTable
            data={allDrivers}
            loading={allLoading}
            onApprove={handleApprove}
            onDocs={setDocsDriverId}
            approvingId={approvingId}
            emptyMessage={emptyMessage}
          />
          <Pagination page={page} limit={PAGE_SIZE} total={allTotal} onPageChange={setPage} />
        </>
      ) : (
        <DriverTable
          data={pendingDrivers}
          loading={pendingLoading}
          onApprove={handleApprove}
          onDocs={setDocsDriverId}
          approvingId={approvingId}
          emptyMessage={emptyMessage}
        />
      )}

      <Drawer
        isOpen={!!docsDriverId}
        onClose={() => setDocsDriverId(null)}
        title="Driver Documents"
      >
        {/* key resets the upload modal / review state when switching drivers */}
        <DocumentReview key={docsDriverId} driverId={docsDriverId} onClose={() => setDocsDriverId(null)} />
      </Drawer>
    </div>
  );
}
